import { Badge, Button, Text } from "@mantine/core";
import { IconPlus } from "@tabler/icons-react";
import type { ColumnDef } from "@tanstack/react-table";
import { useState } from "react";
import { DataTable, PageHeader } from "@how/ui";
import { useCampusOptions } from "../groups/group-queries";
import { CheckInTabs } from "./CheckInTabs";
import { RoomModal } from "./RoomModal";
import { describeAges, useManagedRooms, type ManagedRoom } from "./room-queries";

const PAGE = { pageIndex: 0, pageSize: 200 };

/**
 * The church's check-in rooms (ADR 0042): where children go, the ages each suits and how many it holds. The station
 * suggests a room from a child's age against these ranges. Reached by those who manage events, the permission the
 * database asks for; a row opens the dialog that adds a room.
 */
export function RoomsPage() {
  const rooms = useManagedRooms();
  const campuses = useCampusOptions();
  // undefined: closed. null: adding. A row: editing that room.
  const [open, setOpen] = useState<ManagedRoom | null | undefined>(undefined);
  const status = rooms.isPending ? "loading" : rooms.isError ? "error" : "ready";
  const multiCampus = (campuses.data?.length ?? 0) > 1;

  const columns: ColumnDef<ManagedRoom>[] = [
    {
      accessorKey: "name",
      header: "Room",
      enableSorting: false,
      cell: (ctx) => (
        <Text size="sm" fw={600}>
          {ctx.row.original.name}
        </Text>
      ),
    },
    ...(multiCampus
      ? [
          {
            accessorKey: "campusName",
            header: "Campus",
            enableSorting: false,
            cell: (ctx) =>
              ctx.row.original.campusName ?? (
                <Text size="sm" c="dimmed">
                  Any campus
                </Text>
              ),
          } satisfies ColumnDef<ManagedRoom>,
        ]
      : []),
    {
      id: "ages",
      header: "Ages",
      enableSorting: false,
      cell: (ctx) => describeAges(ctx.row.original.ageMin, ctx.row.original.ageMax),
    },
    {
      accessorKey: "capacity",
      header: "Capacity",
      enableSorting: false,
      meta: { numeric: true },
      cell: (ctx) => ctx.row.original.capacity ?? "No limit",
    },
    {
      accessorKey: "isActive",
      header: "Status",
      enableSorting: false,
      cell: (ctx) =>
        ctx.row.original.isActive ? (
          <Badge variant="light" color="teal">
            Active
          </Badge>
        ) : (
          <Badge variant="light" color="gray">
            Archived
          </Badge>
        ),
    },
  ];

  return (
    <>
      <PageHeader
        title="Check-in"
        description="The rooms children check in to. The station suggests a room from a child's age."
        actions={
          <Button
            leftSection={<IconPlus size={16} />}
            onClick={() => {
              setOpen(null);
            }}
          >
            Add room
          </Button>
        }
      />
      <CheckInTabs active="rooms" />
      <DataTable
        label="Check-in rooms"
        columns={columns}
        rows={rooms.data ?? []}
        totalRows={rooms.data?.length ?? 0}
        status={status}
        pagination={PAGE}
        onPaginationChange={() => undefined}
        sorting={[]}
        onSortingChange={() => undefined}
        getRowId={(row) => row.id}
        onRowClick={(row) => {
          setOpen(row);
        }}
        emptyTitle="No rooms yet"
        emptyDescription="Add the rooms children go to, such as Nursery or Kids room, so the station can suggest one."
        errorMessage="Could not load the rooms."
        onRetry={() => {
          void rooms.refetch();
        }}
      />
      <RoomModal
        key={open === undefined ? "closed" : open === null ? "new" : open.id}
        room={open}
        otherNames={(rooms.data ?? []).filter((r) => r.id !== open?.id).map((r) => r.name)}
        campuses={campuses.data ?? []}
        onClose={() => {
          setOpen(undefined);
        }}
      />
    </>
  );
}
